/**
 * Build {@link CashFlowRequest} payloads for forecasting "full" endpoints,
 * matching the shape used by the Cash Flow module (app/routes/cash-flow.tsx).
 */

import type {
  Asset,
  Cashflow,
  CashFlowRequest,
  LifeEvent,
} from "@/types/cash-flow";

/** Default volatility by asset type (aligned with cash-flow route). */
const DEFAULT_VOLATILITY: Record<string, number> = {
  cash: 0.01,
  pension: 0.15,
  isa: 0.18,
  property: 0.08,
};

function getDefaultVolatility(assetType: string): number {
  return DEFAULT_VOLATILITY[assetType] ?? 0.1;
}

/** Fallback goal when a plan has no goals (matches empty plan in cash-flow route). */
export const EMPTY_CASH_FLOW_GOAL: CashFlowRequest["goal"] = {
  target_value: "0",
  target_assets: [],
  target_period: 20,
};

/**
 * Minimal plan shape for building forecast payloads (matches {@link CashFlowPlan} API).
 */
export interface CashFlowPlanForecastInput {
  settings: {
    surplus_destination: string;
    projection_years: number;
  };
  assets: Asset[];
  cashflows: Cashflow[];
  goals: Array<{
    target_value: number;
    target_period: number;
    target_assets: string[];
  }>;
  scenarios: Array<{
    id: string;
    is_baseline: boolean;
    life_events: LifeEvent[];
  }>;
}

export interface CashFlowGoalSelection {
  target_value: number;
  target_period: number;
  target_assets: string[];
}

/**
 * Resolve the scenario used for projections (explicit id, else baseline, else first).
 *
 * @param plan - Loaded cash flow plan
 * @param scenarioId - Optional scenario id from chart config
 * @returns Scenario or undefined if none
 */
export function resolveCashFlowScenario(
  plan: CashFlowPlanForecastInput,
  scenarioId?: string,
): CashFlowPlanForecastInput["scenarios"][number] | undefined {
  const scenarios = plan.scenarios || [];
  if (scenarioId) {
    const found = scenarios.find((s) => s.id === scenarioId);
    if (found) return found;
  }
  return scenarios.find((s) => s.is_baseline) ?? scenarios[0];
}

/**
 * Build a {@link CashFlowRequest} from persisted plan data for the forecasting API.
 * Uses the given scenario's life events and the selected goal (or first plan goal).
 *
 * @param plan - Plan from getPlan / cache
 * @param scenarioId - Scenario id (optional; baseline used when missing)
 * @param selectedGoal - Goal from chart selection (optional)
 * @returns Payload for forecasting deterministic/stochastic full endpoints
 */
export function buildCashFlowRequestFromPlan(
  plan: CashFlowPlanForecastInput,
  scenarioId: string | undefined,
  selectedGoal: CashFlowGoalSelection | undefined,
): CashFlowRequest {
  const scenario = resolveCashFlowScenario(plan, scenarioId);
  const life_events = scenario?.life_events ?? [];

  const goalPayload: CashFlowRequest["goal"] = selectedGoal
    ? {
        target_value: String(selectedGoal.target_value),
        target_assets: selectedGoal.target_assets || [],
        target_period: selectedGoal.target_period,
      }
    : plan.goals?.[0]
      ? {
          target_value: String(plan.goals[0].target_value),
          target_assets: plan.goals[0].target_assets || [],
          target_period: plan.goals[0].target_period,
        }
      : EMPTY_CASH_FLOW_GOAL;

  return {
    initial_state: {
      surplus_destination: plan.settings?.surplus_destination ?? "cash",
      assets: plan.assets ?? [],
      cashflows: plan.cashflows ?? [],
    },
    settings: {
      type: "years",
      length: plan.settings?.projection_years ?? 20,
    },
    goal: goalPayload,
    life_events,
  };
}

/**
 * Augment a deterministic request for the stochastic "full" endpoint
 * (volatility on assets, iterations, percentiles, optional confidence on goal).
 *
 * @param dataToFetch - Base cash flow request
 * @returns Body for the stochastic multi-asset forecasting endpoint.
 */
export function buildStochasticForecastPayload(
  dataToFetch: CashFlowRequest,
): Record<string, unknown> {
  return {
    ...dataToFetch,
    initial_state: {
      ...dataToFetch.initial_state,
      assets: dataToFetch.initial_state.assets.map((asset) => ({
        ...asset,
        volatility: getDefaultVolatility(asset.type),
      })),
    },
    goal: dataToFetch.goal
      ? { ...dataToFetch.goal, confidence_required: 0.8 }
      : undefined,
    settings: {
      ...dataToFetch.settings,
      model_iterations: 1000,
      percentiles: [10, 25, 50, 75, 90],
    },
  };
}
